import {
  EnvironmentProviders,
  Provider,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  HttpInterceptorFn,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';

import { authInterceptor } from './auth.interceptor';
import { AuthService } from './auth.service';
import { ShellConfigService } from './shell-config.service';
import { SHELL_CONFIG, ShellRuntimeConfig } from './shell-config';

/** Options for {@link provideAuth}. */
export interface AuthOptions {
  /** App-supplied fallback runtime config (apiUrl + environment [+ portalLinks]) used by
   *  {@link ShellConfigService} when /assets/config.json is missing or placeholdered. */
  configFallback: ShellRuntimeConfig;
  /** Extra functional HTTP interceptors, appended AFTER the auth interceptor. */
  extraInterceptors?: HttpInterceptorFn[];
}

/**
 * Frame-independent auth/SSO providers (ADO #1608, Feature #1607). Extracted out of
 * `provideShell` so a portal can consume SSO WITHOUT taking the visual shell:
 *  - provideHttpClient with the bearer/credentials {@link authInterceptor},
 *  - the {@link SHELL_CONFIG} fallback,
 *  - a config-before-auth app initializer (loadConfig FIRST, then the auth probe).
 *
 * Pair with `provideShell()` (frame) when you also want the chrome; `provideShell` relies
 * on the HttpClient provided here (call provideAuth — or provideHttpClient — alongside it).
 *
 * Config-before-auth is strictly sequential: AuthService.checkAuthState() reads
 * config.apiUrl, so it must not run before loadConfig() resolves. loadConfig() is
 * idempotent (returns the cached promise), so the await is cheap.
 */
export function provideAuth(
  options: AuthOptions
): (Provider | EnvironmentProviders)[] {
  return [
    provideHttpClient(
      withInterceptors([authInterceptor, ...(options.extraInterceptors ?? [])])
    ),
    { provide: SHELL_CONFIG, useValue: options.configFallback },
    provideAppInitializer(async () => {
      const config = inject(ShellConfigService);
      const auth = inject(AuthService);
      // Config-before-auth must stay strictly sequential (see doc-comment above).
      await config.loadConfig();
      auth.initAuth();
      await auth.waitForAuthInit();
    }),
  ];
}
